import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Compass, Handshake, LineChart, BarChart3, Wrench, GraduationCap, ArrowUpRight } from "lucide-react";

const services = [
  {
    icon: Compass,
    title: "Aviation Consultancy",
    desc: "Strategic aviation advisory, fleet planning, acquisitions, disposition and investment analysis across commercial, corporate and general aviation.",
    span: "md:col-span-7",
    tag: "01",
  },
  {
    icon: Handshake,
    title: "Aircraft Brokerage",
    desc: "Exclusive mandates, buyer and seller representation, international negotiation and transaction closing.",
    span: "md:col-span-5",
    tag: "02",
  },
  {
    icon: LineChart,
    title: "Asset Management",
    desc: "Valuation, portfolio management, lease monitoring, remarketing and end-of-lease advisory.",
    span: "md:col-span-5",
    tag: "03",
  },
  {
    icon: BarChart3,
    title: "Market Intelligence",
    desc: "Fleet trends, residual forecasts, pricing intelligence, operator analysis and global demand forecasting.",
    span: "md:col-span-7",
    tag: "04",
  },
  {
    icon: Wrench,
    title: "Spare Parts & Tool Trading",
    desc: "Components, rotables, consumables, GSE, special tooling, repair management and global logistics.",
    span: "md:col-span-6",
    tag: "05",
  },
  {
    icon: GraduationCap,
    title: "Flight Training Aircraft",
    desc: "Dedicated division sourcing Cessna 172, Piper Archer PA-28, Diamond DA40 / DA42 and other trainers for FTOs and academies.",
    span: "md:col-span-6",
    tag: "06",
    accent: true,
  },
];

export default function Services() {
  return (
    <section data-testid="services-section" className="relative py-32 md:py-40 border-t border-[#0E1B2E]/10 bg-[#F2F2EF]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 text-[#A5854C] mb-6">
              <div className="hairline w-12"></div>
              <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Services · Full spectrum</span>
            </div>
            <h2 className="font-serif font-light tracking-[-0.02em] text-[#0E1B2E] text-[42px] md:text-[64px] leading-[1.02] max-w-3xl">
              A single advisory partner across the aircraft lifecycle.
            </h2>
          </div>
          <Link to="/services" data-testid="services-see-all" className="btn-ghost px-6 py-3 text-[11px] uppercase tracking-[0.25em] font-mono inline-flex items-center gap-2">
            All services <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className={`col-span-12 ${s.span} p-8 md:p-10 border ${s.accent ? "border-[#C8A97E]/60 bg-[#C8A97E]/10" : "border-[#0E1B2E]/10 bg-white"} group hover:border-[#C8A97E] transition-colors duration-500 relative overflow-hidden`}
                data-testid={`service-card-${s.tag}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE]">Service {s.tag}</span>
                  <Icon size={22} className="text-[#A5854C]" />
                </div>
                <h3 className="mt-16 md:mt-24 font-serif text-[28px] md:text-[36px] tracking-tight text-[#0E1B2E] leading-tight">
                  {s.title}
                </h3>
                <p className="mt-6 max-w-md text-[#6B7684] text-[14px] md:text-[15px] leading-relaxed">
                  {s.desc}
                </p>
                <div className="mt-10 flex items-center gap-2 text-[#A5854C] text-[12px] uppercase tracking-[0.25em] font-mono opacity-70 group-hover:opacity-100 transition-opacity">
                  Learn more <ArrowUpRight size={14} />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
